import type { ReactNode } from 'react';
import { VERIFICATION_LABELS, type LegendaLang, type Verification } from './vocabulary';

export interface VerificationRuleProps {
  status: Verification;
  lang: LegendaLang;
  children?: ReactNode;
  /**
   * Dense tables cannot afford the full status label on every row, and the
   * design forbids truncating one. Set this and the label moves to the table's
   * persistent legend instead — never into a tooltip alone, which would leave
   * the state unreadable in a screenshot.
   */
  labelInLegend?: boolean;
  className?: string;
}

/**
 * The system's core encoding: a left rule whose *pattern* carries confidence.
 *
 * Solid → long-dash → dash → dot is the geologist's own map convention for
 * observed / inferred / concealed contacts, so the ramp reads without being
 * taught. It is drawn in `--lg-ink` with no hue at all, which is what makes it
 * survive greyscale, printing and a screenshot pasted into a report.
 *
 * `conflict`, `unknown` and `rejected` sit deliberately off that ramp — a
 * doubled rule, an empty channel and a struck value — because they are
 * categorical, not degrees of belief.
 *
 * The status is always exposed to assistive technology through `aria-label`,
 * whether or not the visible label is shown.
 */
export default function VerificationRule({
  status,
  lang,
  children,
  labelInLegend = false,
  className,
}: VerificationRuleProps) {
  const label = VERIFICATION_LABELS[status][lang];

  return (
    <span
      className={['lg-vrule', `lg-vrule--${status}`, className].filter(Boolean).join(' ')}
      aria-label={label}
      data-verification={status}
    >
      {children ?? (labelInLegend ? null : label)}
    </span>
  );
}
